import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../utils/api';
import { Shield, Ticket, Camera, X, Smartphone, Wifi } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';

const Login = () => {
  const [voucherCode, setVoucherCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Check if device supports camera
  const [cameraSupported, setCameraSupported] = useState(false);
  // Check for voucher parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const voucherParam = urlParams.get('voucher');
    if (voucherParam) {
      setVoucherCode(voucherParam);
      // Optionally auto-submit the form
      setTimeout(() => {
        handleVoucherSubmit({ preventDefault: () => {} });
      }, 1000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    // Check camera support
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      setCameraSupported(true);
    }
  }, []);

  const handleVoucherSubmit = async (e) => {
    e.preventDefault();
    if (!voucherCode.trim()) {
      toast.error('Please enter a voucher code');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.redeemVoucher(voucherCode.trim());
      const { token, session } = response.data || {};

      if (!token || !session) {
        throw new Error('Portal did not return a session token. Please try again.');
      }

      // Persist auth token + lightweight user profile so ProtectedRoute allows dashboard
      const portalUser = {
        role: 'user',
        display_name: session.plan ? `${session.plan} User` : 'Hotspot User',
        client_ip: session.client_ip,
        session,
      };

      login(portalUser, token);
      localStorage.setItem('active_session', JSON.stringify(session));

      toast.success('🎉 Voucher redeemed! Loading your dashboard…');
      
      // Redirect to dashboard once auth context is populated
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1200);
      
    } catch (error) {
      console.error('Voucher redemption failed:', error);
      const message = error.response?.data?.error || error.message || 'Invalid voucher code';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const startQRScanning = async () => {
    try {
      setShowQRScanner(true);
      
      // Request camera access with mobile-optimized constraints
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' }, // Prefer back camera
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.setAttribute('playsinline', true); // Important for iOS
        videoRef.current.play();
      }

      toast.success('📷 Camera activated! Point at QR code');
      
      // Start QR detection
      startQRDetection();
      
    } catch (error) {
      console.error('Camera access failed:', error);
      toast.error('❌ Camera access denied. Please allow camera permissions.');
      setShowQRScanner(false);
    }
  };

  const stopQRScanning = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowQRScanner(false);
    toast.info('📷 Camera stopped');
  };

  const startQRDetection = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const detectQR = () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        try {
          // Use Web QR API if available, otherwise manual detection
          if (window.BarcodeDetector) {
            const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
            detector.detect(canvas).then(codes => {
              if (codes.length > 0) {
                const qrCode = codes[0].rawValue;
                handleQRCodeDetected(qrCode);
              }
            }).catch(console.error);
          } else {
            // Fallback: Manual QR detection using image data
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const qrCode = detectQRFromImageData(imageData);
            if (qrCode) {
              handleQRCodeDetected(qrCode);
            }
          }
        } catch (error) {
          console.error('QR detection error:', error);
        }
      }
      
      if (showQRScanner) {
        requestAnimationFrame(detectQR);
      }
    };

    detectQR();
  };

  const detectQRFromImageData = (imageData) => {
    // Simple QR code pattern detection
    // This is a basic implementation - in production, use a library like jsQR
    try {
      // Look for QR code patterns in the image data
      // This is a placeholder - implement actual QR detection logic
      return null;
    } catch (error) {
      console.error('Manual QR detection failed:', error);
      return null;
    }
  };

  const handleQRCodeDetected = (qrCode) => {
    console.log('🎯 QR Code detected:', qrCode);
    
    // Validate if it's a voucher code format
    if (qrCode && qrCode.length >= 8) {
      setVoucherCode(qrCode);
      stopQRScanning();
      toast.success('✅ QR Code scanned successfully!');
      
      // Auto-submit the voucher
      setTimeout(() => {
        document.querySelector('form').dispatchEvent(new Event('submit', { bubbles: true }));
      }, 1000);
    } else {
      toast.warning('⚠️ Invalid QR code format');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-6 border-b border-white/10 text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="p-3 bg-blue-500/20 rounded-2xl border border-blue-400/30">
              <Wifi className="h-8 w-8 text-blue-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Sateva Hotspot</h1>
              <p className="text-blue-200 text-sm">Enter voucher for internet access</p>
            </div>
          </div>
        </div>

        {/* QR Scanner Modal */}
        {showQRScanner && (
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
            <div className="relative w-full max-w-sm">
              
              {/* Close button */}
              <button
                onClick={stopQRScanning}
                className="absolute top-4 right-4 z-10 p-2 bg-red-500/80 text-white rounded-full hover:bg-red-600/80 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
              
              {/* Camera viewfinder */}
              <div className="relative bg-black rounded-2xl overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-80 object-cover"
                />
                
                {/* QR scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-48 h-48 border-4 border-green-400 border-dashed rounded-lg animate-pulse">
                    <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
                    <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
                    <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
                  </div>
                </div>
                
                {/* Instructions */}
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <p className="text-white text-sm bg-black/50 rounded-lg py-2 px-4">
                    📷 Point camera at QR code
                  </p>
                </div>
              </div>
              
              <canvas ref={canvasRef} className="hidden" />
            </div>
          </div>
        )}

        {/* Login Form */}
        <div className="p-6">
          <form onSubmit={handleVoucherSubmit} className="space-y-6">
            
            {/* Voucher Input */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-3">
                <Ticket className="inline h-4 w-4 mr-2" />
                Voucher Code
              </label>
              <input
                type="text"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="Enter your voucher code"
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-300 text-center text-lg tracking-wider"
                disabled={loading}
                autoComplete="off"
                autoCapitalize="characters"
              />
            </div>


            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !voucherCode.trim()}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 flex items-center justify-center space-x-2 shadow-lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5" />
                  <span>Connect to Internet</span>
                </>
              )}
            </button>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-white/60 text-sm">
              Need help? Contact support or visit the information desk.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
